import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogActions, DialogContent, DialogTitle, Button, TextField,
  Grid, FormControl, InputLabel, Select, MenuItem, InputAdornment,
  Typography, Box, Tabs, Tab, Alert, Snackbar, CircularProgress, Autocomplete
} from '@mui/material';
import Api from './Api';
import { useAppTheme } from '../../contexts/ThemeContext';

// Returns today's date as "YYYY-MM-DD"
const todayString = () => new Date().toISOString().slice(0, 10);

// Returns current time as "HH:MM"
const currentTimeString = () => {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, '0')}:${String(now.getMinutes()).padStart(2, '0')}`;
};

// Combines date + time strings into an ISO datetime string, or returns null if date is empty
const buildDatetime = (date, time) => {
  if (!date) return null;
  const t = time || '00:00';
  return `${date}T${t}:00`;
};

const RecordPayment = ({ open, onClose, onSuccess }) => {
  const { palette, Colors } = useAppTheme();
  const [paymentType, setPaymentType]   = useState(0);
  const [tenants, setTenants]           = useState([]);
  const [loading, setLoading]           = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [snackbar, setSnackbar]         = useState({ open: false, message: '', severity: 'error' });
  const [formData, setFormData]         = useState({
    tenant_id: '', tenant_name: '', tenant_phone: '',
    amount: '', notes: '', transaction_id: '', payment_method: 'CASH',
    payment_date: todayString(), payment_time: currentTimeString(),
  });

  useEffect(() => {
    if (open) {
      fetchTenants();
      setFormData({
        tenant_id: '', tenant_name: '', tenant_phone: '',
        amount: '', notes: '', transaction_id: '', payment_method: 'CASH',
        payment_date: todayString(), payment_time: currentTimeString(),
      });
      setPaymentType(0);
    }
  }, [open]);

  const fetchTenants = async () => {
    try { setTenants(await Api.getTenantSelection()); }
    catch { setSnackbar({ open: true, message: 'Failed to load tenants', severity: 'error' }); }
  };

  const handleTabChange = (_, v) => {
    setPaymentType(v);
    setFormData({ ...formData, payment_method: ['CASH', 'MPESA', 'BANK'][v], transaction_id: '' });
  };

  const validateForm = () => {
    const { tenant_id, amount, transaction_id, payment_method, payment_date, payment_time } = formData;
    if (!tenant_id)                          { setSnackbar({ open: true, message: 'Please select a tenant',           severity: 'error' }); return false; }
    if (!amount || parseFloat(amount) <= 0)  { setSnackbar({ open: true, message: 'Please enter a valid amount',      severity: 'error' }); return false; }
    if ((payment_method === 'MPESA' || payment_method === 'BANK') && !transaction_id)
                                             { setSnackbar({ open: true, message: 'Please enter a transaction ID',    severity: 'error' }); return false; }
    // If a date is provided, validate it's not in the future
    if (payment_date) {
      const chosen = new Date(`${payment_date}T${payment_time || '00:00'}:00`);
      if (chosen > new Date()) {
        setSnackbar({ open: true, message: 'Payment date/time cannot be in the future', severity: 'error' });
        return false;
      }
    }
    return true;
  };

  const handleConfirmPayment = async () => {
    setLoading(true);
    try {
      const payload = {
        tenant_id: formData.tenant_id,
        amount: parseFloat(formData.amount),
        payment_method: formData.payment_method,
        notes: formData.notes,
        transaction_reference: formData.transaction_id || null,
        payment_datetime: buildDatetime(formData.payment_date, formData.payment_time),
      };
      await Api.recordManualPayment(payload);
      setConfirmDialogOpen(false); setSuccessDialogOpen(true);
      if (onSuccess) onSuccess();
    } catch (err) {
      setSnackbar({ open: true, message: `Failed to record payment: ${err.message}`, severity: 'error' });
      setConfirmDialogOpen(false);
    } finally { setLoading(false); }
  };

  const dialogPaper  = { sx: { backgroundColor: palette.background.secondary, color: palette.text.primary } };
  const primaryBtn   = { sx: { backgroundColor: Colors.primary.main, '&:hover': { backgroundColor: Colors.primary.dark } } };
  const cancelBtn    = { sx: { color: palette.text.secondary } };

  // Format the payment datetime for display in confirm dialog
  const formattedPaymentDateTime = () => {
    if (!formData.payment_date) return 'Now (default)';
    const d = new Date(`${formData.payment_date}T${formData.payment_time || '00:00'}:00`);
    return d.toLocaleString('en-KE', { dateStyle: 'medium', timeStyle: 'short' });
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={dialogPaper}>
        <DialogTitle sx={{ color: palette.text.primary }}>Record Manual Payment</DialogTitle>
        <DialogContent>
          <Box sx={{ borderBottom: 1, borderColor: palette.border, mb: 2 }}>
            <Tabs value={paymentType} onChange={handleTabChange}
              sx={{ '& .Mui-selected': { color: Colors.primary.main }, '& .MuiTabs-indicator': { backgroundColor: Colors.primary.main } }}>
              <Tab label="Cash" sx={{ color: palette.text.secondary }} />
              <Tab label="M-Pesa" sx={{ color: palette.text.secondary }} />
              <Tab label="Bank" sx={{ color: palette.text.secondary }} />
            </Tabs>
          </Box>
          <Grid container spacing={2}>

            {/* Tenant Autocomplete */}
            <Grid item xs={12}>
              <Autocomplete
                options={tenants}
                value={tenants.find(t => t.id === formData.tenant_id) || null}
                getOptionLabel={(t) => `${t.name || `${t.first_name} ${t.last_name}`} - ${t.phone}`}
                filterOptions={(options, { inputValue }) => {
                  const query = inputValue.toLowerCase();
                  return options.filter(t => {
                    const name = (t.name || `${t.first_name} ${t.last_name}`).toLowerCase();
                    return name.includes(query) || t.phone.includes(query);
                  });
                }}
                onChange={(_, selected) => {
                  if (selected) {
                    const fullName = selected.name || `${selected.first_name} ${selected.last_name}`.trim();
                    setFormData({ ...formData, tenant_id: selected.id, tenant_name: fullName, tenant_phone: selected.phone });
                  } else {
                    setFormData({ ...formData, tenant_id: '', tenant_name: '', tenant_phone: '' });
                  }
                }}
                renderInput={(params) => (
                  <TextField {...params} label="Search Tenant" required placeholder="Type name or phone number..." />
                )}
                noOptionsText="No tenants found"
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            {/* M-Pesa Transaction ID */}
            {paymentType === 1 && (
              <Grid item xs={12}>
                <TextField label="M-Pesa Transaction ID" fullWidth required
                  value={formData.transaction_id}
                  onChange={e => setFormData({ ...formData, transaction_id: e.target.value })}
                  placeholder="e.g., QH12ABC345" />
              </Grid>
            )}

            {/* Bank Transaction ID */}
            {paymentType === 2 && (
              <Grid item xs={12}>
                <TextField label="Bank Transaction ID" fullWidth required
                  value={formData.transaction_id}
                  onChange={e => setFormData({ ...formData, transaction_id: e.target.value })}
                  placeholder="Bank reference number" />
              </Grid>
            )}

            {/* Amount */}
            <Grid item xs={12}>
              <TextField label="Amount" type="number" fullWidth required
                value={formData.amount}
                onChange={e => setFormData({ ...formData, amount: e.target.value })}
                InputProps={{ startAdornment: <InputAdornment position="start">KES</InputAdornment> }} />
            </Grid>

            {/* Payment Date + Time (optional) */}
            <Grid item xs={7}>
              <TextField
                label="Payment Date"
                type="date"
                fullWidth
                value={formData.payment_date}
                onChange={e => setFormData({ ...formData, payment_date: e.target.value })}
                inputProps={{ max: todayString() }}
                InputLabelProps={{ shrink: true }}
                helperText="Leave as today or set to actual payment date"
              />
            </Grid>
            <Grid item xs={5}>
              <TextField
                label="Time"
                type="time"
                fullWidth
                value={formData.payment_time}
                onChange={e => setFormData({ ...formData, payment_time: e.target.value })}
                inputProps={{ step: 60 /* 1-minute steps, no seconds */ }}
                InputLabelProps={{ shrink: true }}
                helperText="HH:MM"
              />
            </Grid>

            {/* Notes */}
            <Grid item xs={12}>
              <TextField label="Notes" multiline rows={3} fullWidth
                value={formData.notes}
                onChange={e => setFormData({ ...formData, notes: e.target.value })}
                placeholder={`Additional information about this ${['cash', 'M-Pesa', 'bank'][paymentType]} payment`} />
            </Grid>

          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} {...cancelBtn}>Cancel</Button>
          <Button variant="contained" onClick={() => validateForm() && setConfirmDialogOpen(true)} {...primaryBtn}>Record Payment</Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog */}
      <Dialog open={confirmDialogOpen} onClose={() => setConfirmDialogOpen(false)} PaperProps={dialogPaper}>
        <DialogTitle sx={{ color: palette.text.primary }}>Confirm Payment Details</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom sx={{ color: palette.text.primary }}>Please confirm the following payment details:</Typography>
          <Box sx={{ mt: 2, p: 2, backgroundColor: palette.background.tertiary, borderRadius: 1, border: `1px solid ${palette.border}` }}>
            {[['Payment Method', formData.payment_method],
              formData.tenant_name && ['Tenant', `${formData.tenant_name} (${formData.tenant_phone})`],
              ['Amount', `KES ${formData.amount}`],
              ['Payment Date & Time', formattedPaymentDateTime()],
              formData.transaction_id && ['Transaction ID', formData.transaction_id],
              formData.notes && ['Notes', formData.notes]].filter(Boolean).map(([label, val]) => (
              <Typography key={label} variant="body2" sx={{ color: palette.text.primary, mb: 0.5 }}>
                <strong>{label}:</strong> {val}
              </Typography>
            ))}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDialogOpen(false)} {...cancelBtn}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmPayment} disabled={loading}
            startIcon={loading ? <CircularProgress size={20} /> : null} {...primaryBtn}>
            {loading ? 'Recording...' : 'Confirm'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success dialog */}
      <Dialog open={successDialogOpen} onClose={() => { setSuccessDialogOpen(false); onClose(); }} PaperProps={dialogPaper}>
        <DialogTitle sx={{ color: palette.text.primary }}>Payment Recorded Successfully</DialogTitle>
        <DialogContent>
          <Alert severity="success" sx={{ mb: 2 }}>The payment has been successfully recorded in the system.</Alert>
          <Typography variant="body2" sx={{ color: palette.text.secondary }}>
            Payment of KES {formData.amount} recorded for {formData.payment_method} payment.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button variant="contained" onClick={() => { setSuccessDialogOpen(false); onClose(); }} {...primaryBtn}>OK</Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snackbar.open} autoHideDuration={6000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>{snackbar.message}</Alert>
      </Snackbar>
    </>
  );
};

export default RecordPayment;